import React, { createContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../api/services';
import { useAuth } from './AuthContext';

export const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [theme, setThemeState] = useState(() => {
    // Initialize from localStorage or default to light
    return localStorage.getItem('theme') || 'light';
  });

  // Sync theme when user logs in (use their DB preference)
  useEffect(() => {
    if (isAuthenticated && user?.theme_preference) {
      setThemeState(user.theme_preference);
      localStorage.setItem('theme', user.theme_preference);
    }
  }, [isAuthenticated, user?.theme_preference]);

  // Apply theme to document
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = useCallback(async () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setThemeState(newTheme);

    // Persist to DB if logged in
    if (isAuthenticated) {
      try {
        await authService.updateTheme(newTheme);
      } catch (err) {
        console.error('Failed to save theme preference:', err);
      }
    }
  }, [theme, isAuthenticated]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

